import React, { useCallback, useEffect, useRef } from 'react';

import { LoadableComponent } from '@loadable/component';
import { observe } from 'react-intersection-observer';

import { dealDataToProducts } from '../../../analytics-library/entry/dealDataToProducts';
import { sendEvent } from '../../../analytics-library/entry/sendEvent';
import { WidgetVisible } from '../../../analytics-library/events/WidgetVisible';
import { sendTrackingPixelRequests } from '../../../analytics-library/sendTrackingPixelRequests';
import { ActionType } from '../../../sharedModules/getActionType';
import { LegacyActionEvent } from '../../../types/LegacyActionEvent';
import { IAnalyticsMethods, useAnalytics, WidgetResult } from '../../hooks/useAnalytics';
import type { WidgetProps } from '../../types/WidgetProps';
import { WidgetType } from '../../types/WidgetType';
import { SiteWrapper } from '../SiteWrapper';

import { genericSharedComponents } from './genericSharedComponents';
import styles from './master-widget.css';

const BOOST_BLOCK_VIEW_THRESHOLD = 0.5;
const BOOST_BLOCK_VIEW_TIMER_MS = 1000;
const DEFAULT_VIEW_THRESHOLD = 0;

const sendLegacyVisibleEvent = ({
  sendAnalytics,
  widgetType,
  result,
  dealData,
}: {
  sendAnalytics: IAnalyticsMethods['sendAnalytics'];
  widgetType: WidgetType;
  result: WidgetResult;
  dealData?: WidgetProps['dealData'];
}): void => {
  // Map the result to Google Analytics action type
  const actionType = {
    [WidgetResult.SUCCESS]: ActionType.APPEARED,
    [WidgetResult.MISSING]: ActionType.MISSING,
    [WidgetResult.SKIPPED]: ActionType.SKIPPED,
    [WidgetResult.PRESERVED]: ActionType.PRESERVED,
  }[result];

  // Exception for the Seasonal widget
  if (widgetType === WidgetType.SEASONAL) {
    // For the seasonal widget, track only success result
    if (result !== WidgetResult.SUCCESS) {
      return;
    }
    sendAnalytics({
      category: 'Navigation',
      affiliate: {
        action: {
          event: LegacyActionEvent.VIEWED,
          type: actionType,
        },
      },
      dealData,
    });
    return;
  }

  sendAnalytics({
    affiliate: {
      action: {
        event: LegacyActionEvent.VIEWED,
        type: actionType,
      },
    },
    dealData,
  });
};

// TODO(Petr): Props returned from getInitialiserProps and passed to MasterWidget
//  are not of type WidgetProps, even though the types say so
export type MasterWidgetProps = WidgetProps & {
  Component: LoadableComponent<any>;
  renderFallback: boolean;
  serverSideHtml: string;
  result: WidgetResult;
};

const MasterWidget: React.FC<MasterWidgetProps> = (props) => {
  const { placeholder, widgetType, result, renderFallback, serverSideHtml, site, dealData, name } =
    props;
  const { sendAnalytics } = useAnalytics(props);

  const isBoostBlock = name?.startsWith('boost block');

  const viewedEventSent = useRef(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const sendViewedEvents = useCallback((): void => {
    viewedEventSent.current = true;

    sendTrackingPixelRequests(dealData?.deals ?? []);

    sendLegacyVisibleEvent({
      sendAnalytics,
      widgetType,
      result,
      dealData,
    });

    sendEvent(
      new WidgetVisible({
        widget: {
          type: widgetType,
          result,
          products: dealDataToProducts(dealData),
        },
      }),
    );
  }, [dealData, sendAnalytics, result, widgetType]);

  useEffect(() => {
    const destroyObserver = observe(
      placeholder,
      (inView: boolean) => {
        // Send viewed event only on the first viewport enter
        if (viewedEventSent.current) {
          return;
        }

        if (isBoostBlock) {
          if (inView) {
            // Prevent stacking timers - in the very rare case that the observe callback
            // is called twice in a row with inView=true
            if (timerRef.current) {
              return;
            }

            // Start a timer when ≥ threshold visible
            timerRef.current = setTimeout(() => {
              sendViewedEvents();
            }, BOOST_BLOCK_VIEW_TIMER_MS);
            // Cancel if it leaves viewport before timer expires
          } else if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
          // For all other widgets send the events immediately
        } else if (inView) {
          sendViewedEvents();
        }
      },
      {
        threshold: isBoostBlock ? BOOST_BLOCK_VIEW_THRESHOLD : DEFAULT_VIEW_THRESHOLD,
      },
    );

    return () => {
      destroyObserver();
    };
  }, [
    dealData,
    isBoostBlock,
    name,
    placeholder,
    sendAnalytics,
    sendViewedEvents,
    result,
    widgetType,
  ]);

  if (result === WidgetResult.SKIPPED) {
    return null;
  }

  if (renderFallback) {
    // This results in an extra <div> in the markup, but we cannot skip MasterWidget completely
    // since it adds the tracking functionality. We want to know that a widget entered the viewport,
    // even when it contains a fallback HTML.
    // Adding `all: 'inherit'` is a workaround for this and makes this extra <div> "invisible".
    return (
      // We can disable this warning because we are sure it's a sanitized HTML
      // eslint-disable-next-line react/no-danger
      <div dangerouslySetInnerHTML={{ __html: serverSideHtml }} className={styles.serverFallback} />
    );
  }

  return (
    <SiteWrapper site={site}>
      <props.Component
        {...props}
        genericSharedComponents={genericSharedComponents}
        sendAnalytics={sendAnalytics}
      />
    </SiteWrapper>
  );
};

export default MasterWidget;
